import React from 'react';
import { useInfiniteQuery, useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { personaService, type Persona } from '@/services/personaService';
import { useAuth } from '@/contexts/AuthContext';
import { triggerFeedbackNudge } from '@/lib/feedbackNudge';
import toast from 'react-hot-toast';

export type { Persona };

export function usePersonas() {
    const queryClient = useQueryClient();
    const { user } = useAuth();
    const queryKey = ['personas', user?.id];

    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading: loading, error } = useInfiniteQuery({
        queryKey,
        queryFn: ({ pageParam = 0 }) => personaService.fetchAll(pageParam as number, 50),
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages) => lastPage.length === 50 ? allPages.length * 50 : undefined,
        enabled: !!user,
    });

    const personas = React.useMemo(() => data ? data.pages.flat() : [], [data]);

    const addMutation = useMutation({
        mutationFn: personaService.create,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey });
            toast.success('Persona criada com sucesso!');
            triggerFeedbackNudge();
        },
        onError: () => {
            toast.error('Erro ao criar persona.');
        }
    });

    const updateMutation = useMutation({
        mutationFn: personaService.update,
        onMutate: async (updatedPersona) => {
            await queryClient.cancelQueries({ queryKey });
            const previousPersonas = queryClient.getQueryData<Persona[]>(queryKey);
            
            // Optimistically update
            queryClient.setQueryData(queryKey, (old: InfiniteData<Persona[]> | undefined) => {
                if (!old) return previousPersonas;
                return {
                    ...old,
                    pages: old.pages.map((page: Persona[]) =>
                        page.map(p => p.id === updatedPersona.id ? { ...p, ...updatedPersona } : p)
                    )
                };
            });

            return { previousPersonas };
        },
        onError: (_err, _newVal, context) => {
            if (context?.previousPersonas) {
                queryClient.setQueryData(queryKey, context.previousPersonas);
            }
            toast.error('Erro ao atualizar persona.');
        },
        onSuccess: () => {
            toast.success('Persona atualizada com sucesso!');
            triggerFeedbackNudge();
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: personaService.delete,
        onMutate: async (id) => {
            await queryClient.cancelQueries({ queryKey });
            const previousPersonas = queryClient.getQueryData<Persona[]>(queryKey);
            
            // Optimistically delete
            queryClient.setQueryData(queryKey, (old: InfiniteData<Persona[]> | undefined) => {
                if (!old) return previousPersonas;
                return {
                    ...old,
                    pages: old.pages.map((page: Persona[]) =>
                        page.filter(p => p.id !== id)
                    )
                };
            });

            return { previousPersonas };
        },
        onError: (_err, _newVal, context) => {
            if (context?.previousPersonas) {
                queryClient.setQueryData(queryKey, context.previousPersonas);
            }
            toast.error('Erro ao excluir persona.');
        },
        onSuccess: () => {
            toast.success('Persona excluída com sucesso!');
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey });
        },
    });

    return {
        personas,
        loading,
        error,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        addPersona: addMutation.mutateAsync,
        updatePersona: updateMutation.mutateAsync,
        deletePersona: deleteMutation.mutateAsync,
    };
}
